/**
 * Agenda Wizard
 *
 * A 3-step wizard for creating meeting agendas (dagordningar)
 * according to ABL 7 kap 24-28§.
 *
 * Steps:
 * 1. Meeting Details - Date, time, place
 * 2. Agenda Items - Standard items + custom items
 * 3. Recipients - Email recipients and send
 */

import { useState, useEffect } from "react";
import {
  Calendar,
  MapPin,
  ListOrdered,
  Users,
  Mail,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  GripVertical,
  Send,
} from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import {
  agendasApi,
  type Agenda,
  type AgendaItem,
  type AgendaRecipient,
  type CreateAgendaData,
  type AgendaMeetingType,
} from "@/lib/api";

interface AgendaWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  annualReportId?: string;
  meetingType?: AgendaMeetingType;
  existingAgenda?: Agenda | null; // For editing mode
  onSuccess?: (agendaId: string) => void;
}

type WizardStep = 1 | 2 | 3;

interface MeetingDetails {
  meetingDate: string;
  meetingTime: string;
  meetingPlace: string;
}

// Standard items for årsstämma
const ANNUAL_MEETING_STANDARD_ITEMS = [
  { key: "opening", title: "Stämmans öppnande", required: true },
  { key: "chairman_election", title: "Val av ordförande vid stämman", required: true },
  { key: "voting_list", title: "Upprättande av röstlängd", required: true },
  { key: "adjuster_election", title: "Val av justerare", required: true },
  { key: "agenda_approval", title: "Godkännande av dagordning", required: true },
  { key: "notice_verification", title: "Prövning av kallelse", required: true },
  { key: "annual_report_presentation", title: "Framläggande av årsredovisning", required: true },
  { key: "financial_statements", title: "Fastställande av resultat- och balansräkning", required: true },
  { key: "profit_disposition", title: "Beslut om resultatdisposition", required: true },
  { key: "discharge", title: "Beslut om ansvarsfrihet", required: true },
  { key: "board_election", title: "Val av styrelse", required: false },
  { key: "auditor_election", title: "Val av revisor", required: false },
  { key: "other_business", title: "Övriga ärenden", required: false },
  { key: "closing", title: "Stämmans avslutande", required: true },
];

export function AgendaWizard({
  isOpen,
  onClose,
  companyId,
  annualReportId,
  meetingType = "annual_meeting",
  existingAgenda,
  onSuccess,
}: AgendaWizardProps) {
  const isEditMode = !!existingAgenda;
  const [step, setStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Created agenda (or existing agenda in edit mode)
  const [createdAgenda, setCreatedAgenda] = useState<Agenda | null>(null);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [recipients, setRecipients] = useState<AgendaRecipient[]>([]);

  // Step 1: Meeting Details
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    meetingDate: new Date().toISOString().split("T")[0],
    meetingTime: "18:00",
    meetingPlace: "",
  });

  // Step 2: Items configuration
  const [includeOptionalItems, setIncludeOptionalItems] = useState<string[]>([
    "board_election",
    "auditor_election",
    "other_business",
  ]);
  const [customItems, setCustomItems] = useState<Array<{ title: string; description: string }>>([]);
  const [newItemTitle, setNewItemTitle] = useState("");

  // Step 3: Recipients
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<string[]>([]);
  const [newRecipientName, setNewRecipientName] = useState("");
  const [newRecipientEmail, setNewRecipientEmail] = useState("");

  // Drag and drop state for reordering items
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);

  // Reset state when opening / load existing agenda in edit mode
  useEffect(() => {
    if (isOpen) {
      if (existingAgenda) {
        // Edit mode - load existing agenda data
        setStep(1);
        setCreatedAgenda(existingAgenda);
        setMeetingDetails({
          meetingDate: existingAgenda.meetingDate,
          meetingTime: existingAgenda.meetingTime || "18:00",
          meetingPlace: existingAgenda.meetingPlace || "",
        });
        // Load full agenda with items and recipients
        loadExistingAgendaData(existingAgenda.id);
      } else {
        // Create mode - reset everything
        setStep(1);
        setCreatedAgenda(null);
        setAgendaItems([]);
        setRecipients([]);
        setMeetingDetails({
          meetingDate: new Date().toISOString().split("T")[0],
          meetingTime: "18:00",
          meetingPlace: "",
        });
        setIncludeOptionalItems(["board_election", "auditor_election", "other_business"]);
        setCustomItems([]);
        setNewItemTitle("");
        setSelectedRecipientIds([]);
      }
    }
  }, [isOpen, existingAgenda]);

  // Load existing agenda data for edit mode
  const loadExistingAgendaData = async (agendaId: string) => {
    setIsLoading(true);
    try {
      const response = await agendasApi.get(companyId, agendaId);
      if (response.success && response.data) {
        setAgendaItems(response.data.agenda.items || []);
        setRecipients(response.data.agenda.recipients || []);
        // Set optional items based on what's included
        const standardItemKeys = (response.data.agenda.items || [])
          .filter((item: AgendaItem) => item.standardItemKey)
          .map((item: AgendaItem) => item.standardItemKey as string);
        setIncludeOptionalItems(standardItemKeys.filter(key =>
          ["board_election", "auditor_election", "other_business"].includes(key)
        ));
        // Set custom items
        const customItemsList = (response.data.agenda.items || [])
          .filter((item: AgendaItem) => item.itemType === "custom")
          .map((item: AgendaItem) => ({ title: item.title, description: item.description || "" }));
        setCustomItems(customItemsList);
      }
    } catch (error) {
      console.error("Failed to load agenda:", error);
      toast.error("Kunde inte ladda dagordningen");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    onClose();
  };

  // Step navigation
  const nextStep = async () => {
    if (step === 1) {
      // Create the agenda
      await createAgendaAndGoToStep2();
    } else if (step === 2) {
      // Load recipients and go to step 3
      await loadRecipientsAndGoToStep3();
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep((step - 1) as WizardStep);
    }
  };

  // Step 1 -> 2: Create or update agenda
  const createAgendaAndGoToStep2 = async () => {
    setIsSaving(true);
    try {
      if (isEditMode && createdAgenda) {
        // Update existing agenda
        const updateResponse = await agendasApi.update(companyId, createdAgenda.id, {
          meetingDate: meetingDetails.meetingDate,
          meetingTime: meetingDetails.meetingTime || null,
          meetingPlace: meetingDetails.meetingPlace || null,
        });

        if (updateResponse.success) {
          // Fetch full agenda with items
          const fullResponse = await agendasApi.get(companyId, createdAgenda.id);
          if (fullResponse.success && fullResponse.data) {
            setAgendaItems(fullResponse.data.agenda.items || []);
            setRecipients(fullResponse.data.agenda.recipients || []);
          }
          setStep(2);
        } else {
          toast.error("Kunde inte uppdatera dagordningen");
        }
      } else {
        // Create new agenda
        const data: CreateAgendaData = {
          annualReportId,
          meetingType,
          meetingDate: meetingDetails.meetingDate,
          meetingTime: meetingDetails.meetingTime || undefined,
          meetingPlace: meetingDetails.meetingPlace || undefined,
          includeOptionalItems,
          customItems: customItems.length > 0 ? customItems : undefined,
        };

        const response = await agendasApi.create(companyId, data);

        if (response.success && response.data) {
          setCreatedAgenda(response.data.agenda);

          // Fetch full agenda with items
          const fullResponse = await agendasApi.get(companyId, response.data.agenda.id);
          if (fullResponse.success && fullResponse.data) {
            setAgendaItems(fullResponse.data.agenda.items);
          }

          setStep(2);
        } else {
          toast.error("Kunde inte skapa dagordning");
        }
      }
    } catch (error) {
      console.error("Failed to create/update agenda:", error);
      toast.error("Ett fel uppstod");
    } finally {
      setIsSaving(false);
    }
  };

  // Step 2 -> 3: Load recipients
  const loadRecipientsAndGoToStep3 = async () => {
    if (!createdAgenda) return;

    setIsLoading(true);
    try {
      // Auto-populate recipients
      const response = await agendasApi.autoPopulateRecipients(companyId, createdAgenda.id);

      if (response.success && response.data) {
        setRecipients(response.data.recipients);
        // Select all by default
        setSelectedRecipientIds(response.data.recipients.map((r) => r.id));
      }

      setStep(3);
    } catch (error) {
      console.error("Failed to load recipients:", error);
      toast.error("Kunde inte hämta mottagare");
    } finally {
      setIsLoading(false);
    }
  };

  // Add custom item in step 2
  const handleAddCustomItem = async () => {
    if (!newItemTitle.trim() || !createdAgenda) return;

    setIsSaving(true);
    try {
      const response = await agendasApi.addItem(companyId, createdAgenda.id, {
        title: newItemTitle.trim(),
      });

      if (response.success && response.data) {
        // Refresh items
        const fullResponse = await agendasApi.get(companyId, createdAgenda.id);
        if (fullResponse.success && fullResponse.data) {
          setAgendaItems(fullResponse.data.agenda.items);
        }
        setNewItemTitle("");
        toast.success("Punkt tillagd");
      }
    } catch (error) {
      console.error("Failed to add item:", error);
      toast.error("Kunde inte lägga till punkt");
    } finally {
      setIsSaving(false);
    }
  };

  // Delete custom item
  const handleDeleteItem = async (itemId: string) => {
    if (!createdAgenda) return;

    setIsSaving(true);
    try {
      const response = await agendasApi.deleteItem(companyId, createdAgenda.id, itemId);

      if (response.success) {
        // Refresh items
        const fullResponse = await agendasApi.get(companyId, createdAgenda.id);
        if (fullResponse.success && fullResponse.data) {
          setAgendaItems(fullResponse.data.agenda.items);
        }
        toast.success("Punkt borttagen");
      }
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Kunde inte ta bort punkt");
    } finally {
      setIsSaving(false);
    }
  };

  // Drag and drop handlers for reordering items
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", itemId);
    // Small delay to allow the drag image to be captured before styling changes
    setTimeout(() => {
      setDraggedItemId(itemId);
    }, 0);
  };

  const handleDragOver = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedItemId && targetItemId !== draggedItemId) {
      setDropTargetId(targetItemId);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDropTargetId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const draggedId = draggedItemId || e.dataTransfer.getData("text/plain");

    setDraggedItemId(null);
    setDropTargetId(null);

    if (!draggedId || draggedId === targetItemId || !createdAgenda) {
      return;
    }

    // Reorder items locally first for immediate feedback
    const draggedIndex = agendaItems.findIndex((item) => item.id === draggedId);
    const targetIndex = agendaItems.findIndex((item) => item.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    // Create new order
    const newItems = [...agendaItems];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, draggedItem);

    // Update local state immediately
    setAgendaItems(newItems);

    // Send reorder to server
    try {
      const itemIds = newItems.map((item) => item.id);
      const response = await agendasApi.reorderItems(companyId, createdAgenda.id, itemIds);
      if (!response.success) {
        const errorMsg = typeof response.error === "string" ? response.error : response.error?.message || "Kunde inte ändra ordningen";
        throw new Error(errorMsg);
      }
      // Refresh to get updated item numbers
      const fullResponse = await agendasApi.get(companyId, createdAgenda.id);
      if (fullResponse.success && fullResponse.data) {
        setAgendaItems(fullResponse.data.agenda.items);
      }
    } catch (error) {
      console.error("Failed to reorder items:", error);
      toast.error(error instanceof Error ? error.message : "Kunde inte ändra ordningen");
      // Refresh to restore server state
      const fullResponse = await agendasApi.get(companyId, createdAgenda.id);
      if (fullResponse.success && fullResponse.data) {
        setAgendaItems(fullResponse.data.agenda.items);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDropTargetId(null);
  };

  // Add manual recipient
  const handleAddRecipient = async () => {
    if (!newRecipientName.trim() || !createdAgenda) return;

    setIsSaving(true);
    try {
      const response = await agendasApi.addRecipient(companyId, createdAgenda.id, {
        name: newRecipientName.trim(),
        email: newRecipientEmail.trim() || undefined,
        source: "manual",
      });

      if (response.success && response.data) {
        const newRecipient = response.data.recipient;
        setRecipients([...recipients, newRecipient]);
        if (newRecipient.email) {
          setSelectedRecipientIds([...selectedRecipientIds, newRecipient.id]);
        }
        setNewRecipientName("");
        setNewRecipientEmail("");
        toast.success("Mottagare tillagd");
      }
    } catch (error) {
      console.error("Failed to add recipient:", error);
      toast.error("Kunde inte lägga till mottagare");
    } finally {
      setIsSaving(false);
    }
  };

  // Update recipient email
  const handleUpdateRecipientEmail = async (recipientId: string, email: string) => {
    if (!createdAgenda) return;

    try {
      const response = await agendasApi.updateRecipientEmail(
        companyId,
        createdAgenda.id,
        recipientId,
        email
      );

      if (response.success && response.data) {
        setRecipients(
          recipients.map((r) =>
            r.id === recipientId ? { ...r, email: response.data!.recipient.email } : r
          )
        );
      }
    } catch (error) {
      console.error("Failed to update recipient email:", error);
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (recipientId: string) => {
    const recipient = recipients.find((r) => r.id === recipientId);
    if (!recipient?.email) return; // Can't select recipients without email

    setSelectedRecipientIds((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  // Send emails
  const handleSendEmails = async () => {
    if (!createdAgenda || selectedRecipientIds.length === 0) return;

    setIsSaving(true);
    try {
      const response = await agendasApi.sendEmails(
        companyId,
        createdAgenda.id,
        selectedRecipientIds
      );

      if (response.success && response.data) {
        const successful = response.data.results.filter((r) => r.success).length;
        const failed = response.data.results.filter((r) => !r.success).length;

        if (successful > 0) {
          toast.success(`Dagordning skickad till ${successful} mottagare`);
        }
        if (failed > 0) {
          toast.error(`Kunde inte skicka till ${failed} mottagare`);
        }

        onSuccess?.(createdAgenda.id);
        handleClose();
      }
    } catch (error) {
      console.error("Failed to send emails:", error);
      toast.error("Kunde inte skicka dagordning");
    } finally {
      setIsSaving(false);
    }
  };

  // Save as draft
  const handleSaveAsDraft = () => {
    if (createdAgenda) {
      toast.success("Dagordning sparad som utkast");
      onSuccess?.(createdAgenda.id);
      handleClose();
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (step) {
      case 1:
        return "Mötesuppgifter";
      case 2:
        return "Dagordningspunkter";
      case 3:
        return "Skicka dagordning";
    }
  };

  // Get meeting type label
  const getMeetingTypeLabel = () => {
    switch (meetingType) {
      case "annual_meeting":
        return "årsstämma";
      case "board_meeting":
        return "styrelsemöte";
      case "constitutive_board":
        return "konstituerande styrelsemöte";
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? `Redigera dagordning för ${getMeetingTypeLabel()}` : `Skapa dagordning för ${getMeetingTypeLabel()}`}
      size="2xl"
    >
      <div className="p-6">
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Steg {step} av 3</span>
            <span className="text-sm font-medium">{getStepTitle()}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-sky-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {/* Step 1: Meeting Details */}
        {step === 1 && (
          <div className="space-y-5">
            {/* Date, Time, Place in a nice card */}
            <div className="p-5 bg-white border border-gray-200 rounded-xl shadow-sm space-y-4">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-sky-600" />
                Mötesuppgifter
              </h4>

              <div className="grid sm:grid-cols-2 gap-4">
                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Datum
                  </label>
                  <input
                    type="date"
                    value={meetingDetails.meetingDate}
                    onChange={(e) =>
                      setMeetingDetails({ ...meetingDetails, meetingDate: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                </div>

                {/* Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tid
                  </label>
                  <input
                    type="time"
                    value={meetingDetails.meetingTime}
                    onChange={(e) =>
                      setMeetingDetails({ ...meetingDetails, meetingTime: e.target.value })
                    }
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Place */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <MapPin className="inline h-4 w-4 mr-1 text-gray-400" />
                  Plats
                </label>
                <input
                  type="text"
                  value={meetingDetails.meetingPlace}
                  onChange={(e) =>
                    setMeetingDetails({ ...meetingDetails, meetingPlace: e.target.value })
                  }
                  placeholder="T.ex. Storgatan 1, Stockholm"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Optional items to include */}
            <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <ListOrdered className="h-4 w-4 text-sky-600" />
                Valfria punkter att inkludera
              </h4>
              <div className="space-y-2.5">
                {ANNUAL_MEETING_STANDARD_ITEMS.filter((item) => !item.required).map((item) => {
                  const isChecked = includeOptionalItems.includes(item.key);
                  return (
                    <label
                      key={item.key}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        isChecked ? "bg-sky-50 border border-sky-200" : "bg-white border border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) =>
                            setIncludeOptionalItems(
                              e.target.checked
                                ? [...includeOptionalItems, item.key]
                                : includeOptionalItems.filter((k) => k !== item.key)
                            )
                          }
                          className="peer sr-only"
                        />
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          isChecked
                            ? "bg-sky-600 border-sky-600"
                            : "border-gray-300"
                        }`}>
                          {isChecked && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className={`text-sm ${isChecked ? "text-sky-900 font-medium" : "text-gray-700"}`}>
                        {item.title}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Agenda Items */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
              <ListOrdered className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-sky-800">
                  Dra i handtagen för att ändra ordning på punkterna. Du kan även lägga till egna punkter.
                </p>
              </div>
            </div>

            {/* Items list */}
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
              {agendaItems.map((item, index) => (
                <div
                  key={item.id}
                  draggable={true}
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onDragOver={(e) => handleDragOver(e, item.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, item.id)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center justify-between p-4 transition-all ${
                    index !== agendaItems.length - 1 ? "border-b border-gray-100" : ""
                  } ${
                    draggedItemId === item.id
                      ? "opacity-50 bg-sky-50 scale-[0.98]"
                      : dropTargetId === item.id
                      ? "bg-sky-100 border-sky-300 ring-2 ring-sky-300 ring-inset"
                      : "hover:bg-gray-50"
                  } cursor-grab active:cursor-grabbing select-none`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-1.5 rounded hover:bg-gray-200 transition-colors touch-none">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-gray-600">{item.itemNumber}</span>
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-900 block truncate">{item.title}</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {item.isRequired && (
                          <span className="text-xs text-gray-500">Obligatorisk</span>
                        )}
                        {item.itemType === "custom" && (
                          <span className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-medium">
                            Egen punkt
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {item.itemType === "custom" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(item.id);
                      }}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      disabled={isSaving}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom item */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Lägg till egen punkt
              </h4>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  placeholder="T.ex. Beslut om bemyndigande..."
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  onKeyDown={(e) => e.key === "Enter" && handleAddCustomItem()}
                />
                <button
                  onClick={handleAddCustomItem}
                  disabled={!newItemTitle.trim() || isSaving}
                  className="px-4 py-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Lägg till
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Recipients */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="flex items-start gap-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
              <Mail className="h-5 w-5 text-sky-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-sky-800">
                  Välj vilka som ska få dagordningen via e-post. Du kan också lägga till fler mottagare.
                </p>
              </div>
            </div>

            {/* Recipients list */}
            {recipients.length > 0 ? (
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
                <div className="max-h-72 overflow-y-auto">
                  {recipients.map((recipient, index) => {
                    const isSelected = selectedRecipientIds.includes(recipient.id);
                    const hasEmail = !!recipient.email;
                    const sourceLabel = recipient.source === "shareholder"
                      ? "Aktieägare"
                      : recipient.source === "board_member"
                      ? "Styrelseledamot"
                      : "Manuellt tillagd";
                    const sourceColor = recipient.source === "shareholder"
                      ? "bg-emerald-100 text-emerald-700"
                      : recipient.source === "board_member"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-700";

                    return (
                      <div
                        key={recipient.id}
                        className={`flex items-center gap-4 p-4 transition-colors ${
                          index !== recipients.length - 1 ? "border-b border-gray-100" : ""
                        } ${isSelected ? "bg-sky-50/50" : "hover:bg-gray-50"}`}
                      >
                        <label className="flex items-center gap-4 flex-1 cursor-pointer min-w-0">
                          <div className="relative">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleRecipient(recipient.id)}
                              disabled={!hasEmail}
                              className="peer sr-only"
                            />
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                              !hasEmail
                                ? "border-gray-200 bg-gray-100"
                                : isSelected
                                  ? "bg-sky-600 border-sky-600"
                                  : "border-gray-300 hover:border-sky-400"
                            }`}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-600">
                              {recipient.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 truncate">{recipient.name}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sourceColor}`}>
                                {sourceLabel}
                              </span>
                            </div>
                          </div>
                        </label>
                        <div className="flex-shrink-0">
                          {hasEmail ? (
                            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                              {recipient.email}
                            </span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                              <input
                                type="email"
                                placeholder="Ange e-post..."
                                className="text-sm px-3 py-1.5 border border-amber-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent w-48 bg-amber-50"
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    handleUpdateRecipientEmail(recipient.id, e.target.value);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="text-center py-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-700">Inga mottagare hittades</p>
                <p className="text-sm text-gray-500 mt-1">Lägg till mottagare manuellt nedan</p>
              </div>
            )}

            {/* Add manual recipient */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Lägg till mottagare manuellt
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                <input
                  type="text"
                  value={newRecipientName}
                  onChange={(e) => setNewRecipientName(e.target.value)}
                  placeholder="Namn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
                <input
                  type="email"
                  value={newRecipientEmail}
                  onChange={(e) => setNewRecipientEmail(e.target.value)}
                  placeholder="E-postadress"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
                <button
                  onClick={handleAddRecipient}
                  disabled={!newRecipientName.trim() || isSaving}
                  className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium transition-colors whitespace-nowrap"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Lägg till
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-xl border-2 transition-colors ${
              selectedRecipientIds.length > 0
                ? "bg-sky-50 border-sky-200"
                : "bg-amber-50 border-amber-200"
            }`}>
              <div className={`flex items-center gap-3 ${
                selectedRecipientIds.length > 0 ? "text-sky-700" : "text-amber-700"
              }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  selectedRecipientIds.length > 0 ? "bg-sky-100" : "bg-amber-100"
                }`}>
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">
                    {selectedRecipientIds.length > 0
                      ? `${selectedRecipientIds.length} mottagare valda`
                      : "Inga mottagare valda"}
                  </p>
                  <p className="text-sm opacity-80">
                    {selectedRecipientIds.length > 0
                      ? "Klicka på 'Skicka dagordning' för att skicka"
                      : "Välj minst en mottagare för att kunna skicka"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between mt-8 pt-4">
          <button
            onClick={step === 1 ? handleClose : prevStep}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {step === 1 ? "Avbryt" : "Tillbaka"}
          </button>

          <div className="flex space-x-2">
            {step === 3 && (
              <button
                onClick={handleSaveAsDraft}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Spara som utkast
              </button>
            )}

            {step < 3 ? (
              <button
                onClick={nextStep}
                disabled={isSaving || isLoading}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSaving || isLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <>
                    Fortsätt
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={handleSendEmails}
                disabled={isSaving || selectedRecipientIds.length === 0}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Skicka dagordning
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
